import { Box, Grid } from '@mui/material';
import { EventCarousel } from '@/components/home/event/EventCarousel';
import { FixedButton } from '@/components/common/button/FixedButton';
import { LetterCountDTO } from '@/types/letter/LetterCountDTO';
import { MobileBar } from '@/components/home/MobileBar';
import { Outlet, useOutletContext } from 'react-router-dom';
import { SenderChooseDialog } from '@/components/create/SenderChooseDialog';
import { SenderInfoDTO } from '@/types/create/SenderInfoDTO';
import { SystemEventDTO } from '@/types/event/system/SystemEventDTO';
import { Theme } from '@mui/material/styles';
import { UserDTO } from '@/types/user/UserDTO';
import { convertSystemEvent } from '@/utils/event-utils';
import { getCurrentUser } from '@/services/authSlice';
import { useAppSelector } from '@/app/hooks';
import { useGetNewsQuery, useGetSystemEventsQuery } from '@/services/api/eventApiSlice';
import React, { FC, ReactElement, useState } from 'react';

interface OutletProps {
  inboxCount?: LetterCountDTO;
  outboxCount?: LetterCountDTO;
  senders?: SenderInfoDTO[];
  letterDraftsCount?: number;
  isLoadingSenders?: boolean;
}

export const MobileHomePage: FC = (): ReactElement => {
  const { inboxCount, outboxCount, senders, isLoadingSenders, letterDraftsCount } = useOutletContext<OutletProps>();

  const authUser: UserDTO | undefined = useAppSelector(getCurrentUser);

  const {
    data: events,
    isLoading,
    isFetching,
  } = useGetSystemEventsQuery(authUser?.id, {
    skip: !authUser,
  });

  const { data: news } = useGetNewsQuery();

  const allEvents: SystemEventDTO[] = [...(events || []), ...(news || [])]
    .sort((e1: SystemEventDTO, e2: SystemEventDTO) => {
      const date1: number = e1.timestamp ? new Date(e1.timestamp).getTime() : 0;
      const date2: number = e2.timestamp ? new Date(e2.timestamp).getTime() : 0;
      return date2 - date1;
    })
    .slice(0, 3);

  const loading: boolean = isLoading || isFetching;

  const isSearchFieldEnable = !!(senders && senders.length > 1);

  const [open, setOpen] = useState<boolean>(false);
  const [type, setType] = useState<'letter' | 'answer'>('letter');

  const handleClick = async (type: 'letter' | 'answer'): Promise<void> => {
    setType(type);
    setOpen(true);
  };

  return (
    <Box pb={(theme: Theme) => theme.spacing(6)}>
      <Grid container={true} direction={'column'} wrap={'nowrap'} spacing={5}>
        <Grid item={true}>
          <Grid container={true} direction={'column'} flexWrap={'nowrap'} spacing={3}>
            <Grid item={true}>
              <Box mx={(theme: Theme) => theme.spacing(-2)}>
                <EventCarousel
                  events={(allEvents || []).map((event: SystemEventDTO) => convertSystemEvent(event))}
                  isLoading={loading}
                />
              </Box>
            </Grid>
            <Grid item={true} xs={true}>
              <Outlet context={{ inboxCount, outboxCount, letterDraftsCount, isSearchFieldEnable }} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <MobileBar>
        <Grid container={true} direction={'row'} wrap={'nowrap'}>
          <Grid item={true} xs={true}>
            <FixedButton
              disabled={isLoadingSenders}
              color={'primary'}
              variant={'contained'}
              onClick={() => handleClick('letter')}>
              {'Написать письмо'}
            </FixedButton>
          </Grid>
          <Grid item={true} xs={true}>
            <FixedButton
              disabled={isLoadingSenders}
              color={'primary'}
              variant={'outlined'}
              onClick={() => handleClick('answer')}>
              {'Заказать ответ'}
            </FixedButton>
          </Grid>
        </Grid>
      </MobileBar>
      <SenderChooseDialog
        open={open}
        type={type}
        isMobile={true}
        senders={senders}
        handleCloseButton={() => setOpen(false)}
        onClose={() => setOpen(false)}
      />
    </Box>
  );
};
